/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2024
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import i18n from 'i18next';
import Backend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import languageFromOptions from './i18n-locale-option.js';
import translateCS from '../nls/cs.json';
import translateDE from '../nls/de.json';
import translateEN from '../nls/en.json';
import translateES from '../nls/es.json';
import translateFR from '../nls/fr.json';
import translateIT from '../nls/it.json';
import translateJA from '../nls/ja.json';
import translateKO from '../nls/ko.json';
import translateNL from '../nls/nl.json';
import translatePL from '../nls/pl.json';
import translatePTBR from '../nls/pt-br.json';
import translateRU from '../nls/ru.json';
import translateTR from '../nls/tr.json';
import translateZHCN from '../nls/zh-cn.json';
import translateZHTW from '../nls/zh-tw.json';

export function init(
  options = {
    explorerDir: '/'
  }
) {
  const languageDetector = new LanguageDetector();
  let detectionOptions = { order: ['navigator'] };

  if (options.locale) {
    languageDetector.addDetector(languageFromOptions);

    detectionOptions = {
      order: ['localeOption', 'navigator'],
      locale: options.locale
    };
  }

  const resources = {
    cs: {
      common: translateCS
    },
    de: {
      common: translateDE
    },
    en: {
      common: translateEN
    },
    es: {
      common: translateES
    },
    fr: {
      common: translateFR
    },
    it: {
      common: translateIT
    },
    ja: {
      common: translateJA
    },
    ko: {
      common: translateKO
    },
    nl: {
      common: translateNL
    },
    pl: {
      common: translatePL
    },
    'pt-br': {
      common: translatePTBR
    },
    ru: {
      common: translateRU
    },
    tr: {
      common: translateTR
    },
    'zh-cn': {
      common: translateZHCN
    },
    'zh-tw': {
      common: translateZHTW
    }
  };

  const newInstance = i18n.createInstance();
  newInstance
    .use(Backend)
    .use(languageDetector)
    .use(initReactI18next)
    .init({
      resources,
      detection: detectionOptions,
      fallbackLng: 'en',
      wait: true, // globally set to wait for loaded translations in translate hoc
      lowerCaseLng: true,
      load: 'all',
      // have a common namespace used around the full app
      ns: ['common'],
      defaultNS: 'common',
      interpolation: {
        escapeValue: false, // not needed for react!!
        formatSeparator: ',',
        format: function (value, format, lng) {
          if (format === 'uppercase') return value.toUpperCase();
          return value;
        }
      },
      react: {
        useSuspense: false
      }
    });

  return newInstance;
}
